import { useRef, useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import {
  Alert,
  Badge,
  Button,
  Checkbox,
  cn,
  FilePick,
  Input,
  LinkButton,
  PageHeader,
  Panel,
  PanelBody,
  PanelHeader,
  ProgressBar,
  SectionLabel,
  Segmented,
  Select,
  Table,
  TableShell,
  TBody,
  Td,
  Th,
  THead,
  Tr,
} from '../ui/index.js';
import { int, stamp } from '../lib/format.js';

interface Inspection {
  readonly sheets: readonly string[];
  readonly sheetName: string;
  readonly headerRow: number;
  readonly headers: readonly string[];
  readonly preview: readonly (readonly string[])[];
  readonly totalRows: number;
  readonly suggestions: Readonly<Record<string, string>>;
}

interface TemplateItem {
  readonly id: number;
  readonly name: string;
  readonly sourceKey: string;
  readonly headerRow: number | null;
  readonly columnMap: Record<string, string>;
  readonly defaults: { brand?: string | null };
}

interface SupplierFileItem {
  readonly id: number;
  readonly filename: string;
  readonly source: string;
  readonly profileKey: string;
  readonly sheet: string | null;
  readonly rowsTotal: number;
  readonly rowsOk: number;
  readonly rowsRejected: number;
  readonly importedAt: string;
}

const STEPS = [
  { id: 1, label: 'Analyser', hint: 'feuille, ligne d\u2019en-tête, colonnes' },
  { id: 2, label: 'Mapper', hint: 'chaque colonne vers un champ' },
  { id: 3, label: 'Importer', hint: 'offres créées + CSV des rejets' },
] as const;

const FIELDS: readonly {
  readonly key: string;
  readonly label: string;
  readonly required?: boolean;
}[] = [
  { key: 'name', label: 'Nom / Désignation *', required: true },
  { key: 'gtin', label: 'GTIN (code-barres)' },
  { key: 'reference', label: 'Référence fournisseur' },
  { key: 'brand', label: 'Marque' },
  { key: 'priceHt', label: 'Prix HT' },
  { key: 'priceTtc', label: 'Prix TTC' },
  { key: 'vatRate', label: 'TVA %' },
  { key: 'promo', label: 'Promo quantité (ex. 5+1)' },
  { key: 'pricePublic', label: 'Prix public (avant promo)' },
  { key: 'stockQty', label: 'Stock (quantité)' },
  { key: 'image', label: 'Image (URL)' },
];

async function fetchJson<T>(path: string, init?: RequestInit): Promise<T> {
  const hasBody = init?.body !== undefined && init.body !== null;
  const isFormData = typeof FormData !== 'undefined' && init?.body instanceof FormData;
  const headers = new Headers(init?.headers);
  if (!headers.has('accept')) {
    headers.set('accept', 'application/json');
  }
  if (hasBody && !isFormData && !headers.has('content-type')) {
    headers.set('content-type', 'application/json');
  }
  const response = await fetch(path, { credentials: 'include', ...init, headers });
  if (!response.ok) {
    const body = (await response.json().catch(() => ({}))) as { error?: string; detail?: string };
    throw new Error(
      [body.error, body.detail].filter(Boolean).join(' — ') || `HTTP ${response.status}`,
    );
  }
  return (await response.json()) as T;
}

/** S06 — Supplier files: import wizard for ANY price list (column mapping + templates). */
export function SupplierFilesPage(): React.ReactElement {
  const queryClient = useQueryClient();
  const fileInput = useRef<HTMLInputElement>(null);
  const [message, setMessage] = useState<string | null>(null);
  const [phase, setPhase] = useState<'idle' | 'upload' | 'processing' | 'done'>('idle');
  const [progress, setProgress] = useState(0);
  const [inspection, setInspection] = useState<Inspection | null>(null);
  const [headerRow, setHeaderRow] = useState<number | null>(null);
  const [mapping, setMapping] = useState<Record<string, string>>({});
  const [templateName, setTemplateName] = useState('');
  const [saveTemplate, setSaveTemplate] = useState(false);
  const [sourceKey, setSourceKey] = useState('wholesaler-file');

  const templatesQuery = useQuery({
    queryKey: ['templates'],
    queryFn: () => fetchJson<readonly TemplateItem[]>('/api/v1/templates'),
  });
  const listQuery = useQuery({
    queryKey: ['supplier-files'],
    queryFn: () => fetchJson<readonly SupplierFileItem[]>('/api/v1/supplier-files'),
  });

  const applyTemplate = (name: string): void => {
    const template = templatesQuery.data?.find((item) => item.name === name);
    if (template === undefined) return;
    setMapping({ ...template.columnMap });
    setHeaderRow(template.headerRow);
    setSourceKey(template.sourceKey);
    setTemplateName(template.name);
  };
  const inspectMutation = useMutation({
    mutationFn: async (headerRowOverride: number | null) => {
      const file = fileInput.current?.files?.[0];
      if (file === undefined) throw new Error('choisissez un fichier');
      const form = new FormData();
      if (headerRowOverride !== null) form.set('headerRow', String(headerRowOverride));
      form.set('file', file);
      setPhase('upload');
      setProgress(0);
      return await new Promise<Inspection>((resolve, reject) => {
        const xhr = new XMLHttpRequest();
        xhr.open('POST', '/api/v1/import/inspect');
        xhr.responseType = 'json';
        xhr.withCredentials = true;
        xhr.upload.onprogress = (event) => {
          if (event.lengthComputable) setProgress(Math.round((event.loaded / event.total) * 100));
        };
        xhr.onload = () => {
          if (xhr.status >= 200 && xhr.status < 300) resolve(xhr.response as never);
          else
            reject(new Error((xhr.response as { error?: string })?.error ?? `HTTP ${xhr.status}`));
        };
        xhr.onerror = () => reject(new Error('réseau indisponible'));
        xhr.send(form);
      });
    },
    onSuccess: (result) => {
      setPhase('idle');
      setInspection(result);
      setHeaderRow(result.headerRow);
      const suggested = Object.fromEntries(
        Object.entries(result.suggestions ?? {}).map(([field, column]) => [field, column]),
      );
      if (suggested['name'] === undefined) {
        const firstColumn = result.headers.find((header) => header !== '');
        if (firstColumn !== undefined) suggested['name'] = firstColumn;
      }
      setMapping(suggested);
      setMessage(null);
    },
    onError: (error) => {
      setPhase('idle');
      setMessage(`Analyse impossible : ${String(error.message)}`);
    },
  });

  const importMutation = useMutation({
    mutationFn: async () => {
      const file = fileInput.current?.files?.[0];
      if (file === undefined) throw new Error('choisissez un fichier');
      if (mapping['name'] === undefined || mapping['name'] === '') {
        throw new Error('mappez la colonne Nom');
      }
      const form = new FormData();
      form.set('sourceKey', sourceKey);
      form.set(
        'mapping',
        JSON.stringify({
          templateName: templateName.trim() === '' ? null : templateName.trim(),
          saveTemplate: saveTemplate && templateName.trim() !== '',
          headerRow,
          columnMap: mapping,
          defaults: { brand: null },
        }),
      );
      form.set('file', file);
      setPhase('upload');
      setProgress(0);
      return await new Promise<{
        status: string;
        rowsOk: number;
        rowsNew: number;
        rowsUpdated: number;
        rowsTotal: number;
        rowsRejected: number;
        rejectSummary: Record<string, number>;
        supplierFileId: number;
      }>((resolve, reject) => {
        const xhr = new XMLHttpRequest();
        xhr.open('POST', '/api/v1/supplier-files');
        xhr.responseType = 'json';
        xhr.withCredentials = true;
        xhr.upload.onprogress = (event) => {
          if (event.lengthComputable) setProgress(Math.round((event.loaded / event.total) * 100));
        };
        xhr.upload.onload = () => setPhase('processing');
        xhr.onload = () => {
          if (xhr.status >= 200 && xhr.status < 300) resolve(xhr.response as never);
          else {
            const body = xhr.response as { error?: string; detail?: string } | null;
            reject(new Error(body?.detail ?? body?.error ?? `HTTP ${xhr.status}`));
          }
        };
        xhr.onerror = () => reject(new Error('réseau indisponible'));
        xhr.send(form);
      });
    },
    onSuccess: (result) => {
      setPhase('done');
      const reasons = Object.entries(result.rejectSummary ?? {})
        .map(([reason, count]) => `${reason} ×${count}`)
        .join(', ');
      setMessage(
        `Importé : ${result.rowsOk} offres (${result.rowsNew ?? result.rowsOk} nouvelles, ${result.rowsUpdated ?? 0} mises à jour), ${result.rowsRejected} rejetées sur ${result.rowsTotal}.` +
          (reasons !== '' ? ` Raisons : ${reasons} — téléchargez le CSV des rejets.` : ''),
      );
      void queryClient.invalidateQueries({ queryKey: ['supplier-files'] });
      void queryClient.invalidateQueries({ queryKey: ['templates'] });
    },
    onError: (error) => {
      setPhase('idle');
      setMessage(`Échec : ${String(error.message)}`);
    },
  });

  const deleteMutation = useMutation({
    mutationFn: async (fileId: number) => {
      const res = await fetch(`/api/v1/supplier-files/${fileId}`, {
        method: 'DELETE',
        credentials: 'include',
      });
      if (!res.ok) {
        const body = (await res.json()) as { error?: string } | null;
        throw new Error(body?.error ?? `HTTP ${res.status}`);
      }
      return (await res.json()) as { offersDeleted: number };
    },
    onSuccess: (data) => {
      setMessage(`Fichier et ${data.offersDeleted} offres supprimés avec succès.`);
      void queryClient.invalidateQueries({ queryKey: ['supplier-files'] });
    },
    onError: (error) => {
      setMessage(`Erreur de suppression : ${String(error.message)}`);
    },
  });

  const busy = inspectMutation.isPending || importMutation.isPending || deleteMutation.isPending;
  const history = listQuery.data ?? [];
  const currentStep = phase === 'done' ? 4 : inspection === null ? 1 : 2;

  return (
    <div>
      <PageHeader
        index="S06"
        title="Fichiers fournisseurs"
        purpose="Assistant d'import de n'importe quelle liste de prix : déposez le fichier, vérifiez la ligne d'en-tête, mappez les colonnes (suggestions automatiques) puis importez. Enregistrez le mapping comme modèle pour la prochaine liste du même fournisseur."
        meta={[
          { label: 'Imports', value: int(history.length) },
          { label: 'Modèles', value: int(templatesQuery.data?.length ?? 0) },
          {
            label: 'Étape',
            value: currentStep === 4 ? 'terminée' : `${currentStep} / 3`,
          },
        ]}
      />

      <ol className="mb-4 grid gap-2.5 sm:grid-cols-3">
        {STEPS.map((step) => {
          const done = currentStep > step.id;
          const active = currentStep === step.id;
          return (
            <li
              key={step.id}
              className={cn(
                'shadow-xs flex items-center gap-3 rounded-xl border px-3.5 py-2.5 transition-all',
                active
                  ? 'border-herb-600/40 bg-herb-50/40 ring-1 ring-herb-500/20'
                  : done
                    ? 'border-herb-600/30 bg-card'
                    : 'border-border bg-card',
              )}
            >
              <span
                className={cn(
                  'grid h-6 w-6 shrink-0 place-items-center rounded-md border font-mono text-[11px] font-bold',
                  active
                    ? 'border-herb-600 bg-herb-600 text-white'
                    : done
                      ? 'border-herb-600 bg-herb-50 text-herb-700'
                      : 'border-border bg-cream text-ink-ghost',
                )}
              >
                {done ? '✓' : step.id}
              </span>
              <span className="min-w-0">
                <span className="block text-xs font-semibold uppercase tracking-wider text-ink">
                  {step.label}
                </span>
                <span className="block text-xs text-ink-soft">{step.hint}</span>
              </span>
            </li>
          );
        })}
      </ol>

      <Panel className="mb-4">
        <PanelHeader
          index="1"
          title="Fichier source"
          hint=".xlsx, .xls ou .csv — une feuille à la fois"
          actions={
            templatesQuery.data !== undefined && templatesQuery.data.length > 0 ? (
              <Select
                aria-label="Charger un modèle de mapping"
                defaultValue=""
                className="h-8 w-56 py-0 text-xs"
                onChange={(event) => applyTemplate(event.target.value)}
              >
                <option value="">— Charger un modèle —</option>
                {templatesQuery.data.map((template) => (
                  <option key={template.id} value={template.name}>
                    {template.name}
                  </option>
                ))}
              </Select>
            ) : undefined
          }
        />
        <PanelBody className="flex flex-wrap items-center gap-3">
          <FilePick
            inputRef={fileInput}
            accept=".xlsx,.xls,.csv"
            title="Liste de prix fournisseur"
            hint="plusieurs feuilles ? enregistrez-en une par fichier"
          />
          <Button
            type="button"
            variant="primary"
            size="md"
            className="ml-auto"
            disabled={busy}
            onClick={() => {
              setMessage(null);
              inspectMutation.mutate(null);
            }}
          >
            {inspectMutation.isPending ? 'Analyse…' : 'Analyser le fichier'}
          </Button>
          {busy && (
            <ProgressBar
              className="w-full max-w-md"
              value={phase === 'processing' ? null : progress}
              label={phase === 'processing' ? 'Traitement des lignes…' : 'Transfert du fichier'}
            />
          )}
        </PanelBody>
      </Panel>

      {message !== null && (
        <Alert
          tone={
            message.startsWith('Échec') || message.startsWith('Analyse impossible')
              ? 'alarm'
              : 'good'
          }
          title={
            message.startsWith('Échec') || message.startsWith('Analyse impossible')
              ? 'Import interrompu'
              : 'Journal d\u2019import'
          }
          className="mb-4"
        >
          {message}
        </Alert>
      )}
      {inspection !== null && (
        <Panel className="mb-4" tone={phase === 'done' ? 'good' : 'neutral'}>
          <PanelHeader
            index="2"
            title="Mapping des colonnes"
            hint={`feuille ${inspection.sheetName} · ${int(inspection.totalRows)} lignes de données`}
            actions={
              <>
                <label className="flex items-center gap-2 font-mono text-[10px] uppercase tracking-[0.14em] text-ink-soft">
                  Ligne d&apos;en-tête
                  <Input
                    type="number"
                    min={1}
                    value={headerRow ?? ''}
                    onChange={(event) => setHeaderRow(Number(event.target.value) || null)}
                    className="h-8 w-16 py-0 text-center text-xs"
                  />
                </label>
                <Button
                  type="button"
                  variant="outline"
                  size="xs"
                  disabled={busy}
                  onClick={() => inspectMutation.mutate(headerRow)}
                >
                  Ré-analyser
                </Button>
              </>
            }
          />
          <PanelBody>
            <div className="flex flex-wrap items-center gap-2 font-mono text-[10px] uppercase tracking-[0.14em] text-ink-faint">
              <span>
                {inspection.sheets.length > 1
                  ? `Feuilles détectées : ${inspection.sheets.join(' · ')}`
                  : 'Feuille unique'}
              </span>
              <span className="ml-auto">
                {FIELDS.filter((field) => mapping[field.key] !== undefined).length} champs mappés
                sur {FIELDS.length}
              </span>
            </div>

            <SectionLabel className="mt-4">Champs cibles</SectionLabel>
            <div className="mt-2 grid gap-3 sm:grid-cols-2 lg:grid-cols-4">
              {FIELDS.map((field) => (
                <label key={field.key} className="flex flex-col gap-1">
                  <span
                    className={
                      'label-narrow font-mono text-[10px] uppercase tracking-[0.14em] ' +
                      (field.required === true && mapping[field.key] === undefined
                        ? 'text-rust-600'
                        : 'text-ink-soft')
                    }
                  >
                    {field.label}
                  </span>
                  <Select
                    value={mapping[field.key] ?? ''}
                    onChange={(event) =>
                      setMapping((current) => ({ ...current, [field.key]: event.target.value }))
                    }
                    className={
                      field.required === true && mapping[field.key] === undefined
                        ? 'border-rust-600'
                        : ''
                    }
                  >
                    <option value="">— ignorer —</option>
                    {inspection.headers
                      .filter((header) => header !== '')
                      .map((header) => (
                        <option key={header} value={header}>
                          {header}
                        </option>
                      ))}
                  </Select>
                </label>
              ))}
            </div>
            <SectionLabel className="mt-5">Aperçu (colonnes mappées)</SectionLabel>
            <TableShell plain className="mt-2 rounded-panel border border-border">
              <Table>
                <THead>
                  <tr>
                    {FIELDS.filter((field) => mapping[field.key] !== undefined).map((field) => (
                      <Th key={field.key}>{field.label}</Th>
                    ))}
                  </tr>
                </THead>
                <TBody>
                  {inspection.preview.map((cells, index) => {
                    const record = Object.fromEntries(
                      inspection.headers.map((header, position) => [header, cells[position] ?? '']),
                    );
                    return (
                      <Tr key={index}>
                        {FIELDS.filter((field) => mapping[field.key] !== undefined).map((field) => {
                          const column = mapping[field.key];
                          return (
                            <Td key={field.key} className="font-mono text-xs text-ink">
                              {column === undefined ? '' : (record[column] ?? '')}
                            </Td>
                          );
                        })}
                      </Tr>
                    );
                  })}
                </TBody>
              </Table>
            </TableShell>

            <SectionLabel className="mt-5">Enregistrer et importer</SectionLabel>
            <div className="mt-2 flex flex-wrap items-end gap-3">
              <label className="flex flex-col gap-1">
                <span className="label-narrow font-mono text-[10px] uppercase tracking-[0.14em] text-ink-soft">
                  Nom du modèle
                </span>
                <Input
                  type="text"
                  value={templateName}
                  onChange={(event) => setTemplateName(event.target.value)}
                  placeholder="ex. allpharm-2026"
                  className="w-56"
                />
              </label>
              <Checkbox
                checked={saveTemplate}
                label="Enregistrer ce mapping comme modèle"
                onChange={(event) => setSaveTemplate(event.target.checked)}
              />
              <label className="flex flex-col gap-1">
                <span className="label-narrow font-mono text-[10px] uppercase tracking-[0.14em] text-ink-soft">
                  Source des offres
                </span>
                <Segmented
                  ariaLabel="Source des offres"
                  value={sourceKey}
                  onChange={setSourceKey}
                  options={[
                    { value: 'wholesaler-file', label: 'Grossiste' },
                    { value: 'depot-file', label: 'Dépôt' },
                  ]}
                />
              </label>
              <Button
                type="button"
                variant="primary"
                size="md"
                className="ml-auto"
                disabled={busy || mapping['name'] === undefined}
                onClick={() => {
                  setMessage(null);
                  importMutation.mutate();
                }}
              >
                {importMutation.isPending ? 'Import…' : 'Importer les offres'}
              </Button>
            </div>
            <p className="mt-2 text-[11px] text-ink-faint">
              L&apos;import ne touche jamais le site : il crée des offres fournisseurs (prix
              d&apos;achat candidats) que le matching rattachera ensuite aux produits.
            </p>
          </PanelBody>
        </Panel>
      )}
      <Panel>
        <PanelHeader
          index="↺"
          title="Historique des imports"
          hint="traçabilité fichier → offres créées"
          actions={<Badge tone="muted">{int(history.length)} fichiers</Badge>}
        />
        {listQuery.isPending && (
          <PanelBody>
            <p className="text-sm text-ink-soft">Lecture de l&apos;historique…</p>
          </PanelBody>
        )}
        {listQuery.data !== undefined && history.length === 0 && (
          <PanelBody>
            <p className="text-sm text-ink-soft">
              Aucun import enregistré — déposez une liste de prix ci-dessus pour créer les premières
              offres.
            </p>
          </PanelBody>
        )}
        {history.length > 0 && (
          <TableShell plain>
            <Table>
              <THead>
                <tr>
                  <Th>#</Th>
                  <Th>Fichier</Th>
                  <Th>Mapping</Th>
                  <Th align="right">Lignes</Th>
                  <Th align="right">Créées</Th>
                  <Th align="right">Rejetées</Th>
                  <Th>Date</Th>
                  <Th />
                </tr>
              </THead>
              <TBody>
                {history.map((item) => (
                  <Tr key={item.id}>
                    <Td className="tabular font-mono text-xs text-ink-soft">{item.id}</Td>
                    <Td className="max-w-[22rem] truncate text-ink">
                      {item.filename}
                      {item.sheet !== null && (
                        <span className="ml-2 font-mono text-[10px] text-ink-faint">
                          {item.sheet}
                        </span>
                      )}
                    </Td>
                    <Td>
                      <Badge tone="muted">{item.profileKey}</Badge>
                    </Td>
                    <Td align="right" className="tabular font-mono text-ink-soft">
                      {int(item.rowsTotal)}
                    </Td>
                    <Td align="right" className="tabular font-mono text-herb-700">
                      {int(item.rowsOk)}
                    </Td>
                    <Td align="right" className="tabular font-mono">
                      <span className={item.rowsRejected > 0 ? 'text-rust-600' : 'text-ink-soft'}>
                        {int(item.rowsRejected)}
                      </span>
                    </Td>
                    <Td className="font-mono text-xs text-ink-soft">{stamp(item.importedAt)}</Td>
                    <Td align="right">
                      <div className="flex items-center justify-end gap-2">
                        {item.rowsRejected > 0 && (
                          <LinkButton
                            href={`/api/v1/supplier-files/${item.id}/rejects`}
                            variant="outline"
                            size="xs"
                          >
                            rejets CSV
                          </LinkButton>
                        )}
                        <Button
                          variant="ghost"
                          size="xs"
                          className="text-rust-600 hover:text-rust-700 hover:bg-rust-500/10"
                          disabled={busy}
                          onClick={() => {
                            if (
                              window.confirm(
                                `Supprimer définitivement "${item.filename}" et ses offres associées ?`,
                              )
                            ) {
                              deleteMutation.mutate(item.id);
                            }
                          }}
                        >
                          Supprimer
                        </Button>
                      </div>
                    </Td>
                  </Tr>
                ))}
              </TBody>
            </Table>
          </TableShell>
        )}
      </Panel>
    </div>
  );
}
